import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import {Sort} from '@angular/material/sort';
import {TranslateService} from '@ngx-translate/core';
import {GateweeTableColumn, TableAction} from '@gatewee/interfaces';
import {PermissionPipe} from '@gatewee/helpers/pipes/permission.pipe';

@Component({
  selector: 'gatewee-table',
  templateUrl: './gatewee-table.component.html',
  styleUrls: ['./gatewee-table.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GateweeTableComponent<T> implements OnChanges, OnInit {
  @Input() data: T[] = [];
  @Input() columns!: GateweeTableColumn[];
  @Input() page = 1;
  @Input() customClass = '';
  @Input() customClassTable = '';
  @Input() pageSize = 10;
  @Input() hasCheckbox = false;
  @Input() total = 1;
  @Input() showSettings = false;
  @Input() tableId = 'default';

  @Input() rowDetailTpl: TemplateRef<{$implicit: T}> | null = null;
  @Input() expandableWhen?: (row: T) => boolean;
  @Input() showExpandColumn = true;

  @Input() rowIdFn: (row: T, index: number) => string | number = (_r, i) => i;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() getActionDisabled: (row: any, key: string) => boolean = () => false;

  @Output() newPage = new EventEmitter<string>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() outClick = new EventEmitter<{row: T; type: string}>();

  constructor(
    private translate: TranslateService,
    private permissionPipe: PermissionPipe,
  ) {}

  totalRows = 0;
  totalPages = 0;
  totalPerPage = 0;
  selectAll = false;
  boxActive = false;

  private expanded = new Set<string | number>();

  trackByField = (_: number, col: GateweeTableColumn) => col.field;

  private calculatePagination(): void {
    this.totalRows = this.total;
    this.totalPages = Math.ceil(this.totalRows / this.pageSize);
    this.totalPerPage = this.pageSize;
  }

  private getStorageKey(): string {
    return this.tableId;
  }

  private isMobileDevice(): boolean {
    // Check screen width (Bootstrap mobile breakpoint is 768px)
    const isMobileWidth = window.innerWidth < 768;

    // Check user agent for mobile devices
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );

    return isMobileWidth || isMobileUserAgent;
  }

  ngOnInit(): void {
    const saved = localStorage.getItem(this.getStorageKey());
    const isMobile = this.isMobileDevice();

    // 🔹 Caso showSettings seja falso → mostrar todas as colunas
    if (!this.showSettings) {
      this.columns = this.columns.map(col => ({
        ...col,
        visible: true,
      }));
      return;
    }

    // 🔹 Caso contrário, segue a lógica normal
    if (saved) {
      try {
        const prefs: {field: string; visible: boolean}[] = JSON.parse(saved);

        this.columns = this.columns.map(col => {
          const pref = prefs.find(p => p.field === col.field);

          // If alwaysVisible is true, column must be visible regardless of saved preference
          // Otherwise, use the saved preference if it exists, or default to false
          const visible =
            col.alwaysVisible === true ? true : pref?.visible ?? false;

          return {...col, visible};
        });
      } catch (e) {
        console.error('Erro ao restaurar preferências de colunas', e);
      }
    } else {
      // No saved preferences
      this.columns = this.columns.map(col => {
        let visible: boolean;

        if (isMobile) {
          // On mobile without saved preferences, only show alwaysVisible columns and actions
          visible = col.alwaysVisible === true || col.field === 'actions';
        } else {
          // On desktop without saved preferences, show alwaysVisible or default visible
          visible = col.alwaysVisible ?? col.visible ?? false;
        }

        return {...col, visible};
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.calculatePagination();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onColumnToggle(_col: GateweeTableColumn): void {
    const prefs = this.columns.map(c => ({field: c.field, visible: c.visible}));
    localStorage.setItem(this.getStorageKey(), JSON.stringify(prefs));
  }

  computeColspan(): number {
    let span = this.columns.filter(
      c => c.visible && c.field !== 'actions',
    ).length;
    if (this.hasCheckbox) span += 1;
    if (this.showExpandColumn && this.rowDetailTpl) span += 1;
    span += 1;
    return span;
  }

  getValueFromPath(obj: T, path: string, raw = false) {
    const def = this.columns.find(e => e.field === path);

    const value = path
      .split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((acc: unknown, part: string) => (acc as any)?.[part], obj);
    if (def?.pipe && !raw) {
      try {
        return def.pipe.fn.transform?.(value, ...def.pipe.args) ?? value;
      } catch (e) {
        return value;
      }
    } else {
      return value;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getStatusClass(_status: string) {
    return '';
  }

  checkUncheckAll(): void {
    this.boxActive = !this.boxActive;
  }

  pageChange(e: string) {
    this.page = +e;
    this.newPage.emit(e);
  }

  onSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
  }

  toggleSelectAll(): void {
    this.selectAll = !this.selectAll;
  }

  onClick(row: T, type = 'delete'): void {
    this.outClick.emit({row, type});
  }

  getSummary(): string {
    const from = this.pageSize * (this.page - 1) + 1;
    const to = Math.min(this.pageSize * this.page, this.totalRows);
    return this.translate.instant('common.paginationSummary', {
      from,
      to,
      total: this.totalRows,
    });
  }

  isActionsColumn(
    col: GateweeTableColumn,
  ): col is GateweeTableColumn & {field: 'actions'} {
    return col.field === 'actions';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onActionClick(row: any, action: TableAction) {
    this.onClick(row, action.key);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  canExpandRow(row: T, _index: number): boolean {
    if (!this.rowDetailTpl || !this.showExpandColumn) return false;
    return this.expandableWhen ? !!this.expandableWhen(row) : true;
  }

  toggleExpand(row: T, index: number): void {
    if (!this.canExpandRow(row, index)) return;
    const id = this.rowIdFn(row, index);
    this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id);
  }

  isExpanded(row: T, index: number): boolean {
    return this.expanded.has(this.rowIdFn(row, index));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowActions(invoice: any) {
    const base = this.columns.find(c => c.field === 'actions')?.actions ?? [];
    return base.map(action => ({
      ...action,
      disabled: this.getActionDisabled(invoice, action.key),
    }));
  }

  hasMenu(row: T): boolean {
    const actionsCol = this.columns.find(c => c.field === 'actions');
    if (!actionsCol || !actionsCol.actions) return false;

    return actionsCol.actions.some(action => {
      if (!action.condition) return true;
      return this.evaluateActionCondition(row, action);
    });
  }

  evaluateActionCondition(row: T, action: TableAction): boolean {
    if (!action.condition) return true;

    return Object.keys(action.condition).every(key => {
      const value = action.condition?.[key];

      if (key === 'permission') {
        return this.permissionPipe.transform(value as string);
      }

      if (
        action?.condition?.[key] === true &&
        row[key as keyof T] !== undefined
      )
        return true;

      let isNot = false;
      let expectedValue = value;

      if (typeof value === 'string' && value.startsWith('!')) {
        isNot = true;
        expectedValue = value.slice(1);
      }

      const actualValue = this.getValueFromPath(row, key, true);

      if (expectedValue === '__exists__') {
        return actualValue !== undefined && actualValue !== null;
      }

      if (Array.isArray(expectedValue)) {
        return isNot
          ? !expectedValue.includes(actualValue)
          : expectedValue.includes(actualValue);
      }

      return isNot
        ? actualValue !== expectedValue
        : actualValue === expectedValue;
    });
  }
}
